
import React, { useState, useEffect } from 'react';
import { useTranslationUtils } from '@/hooks/useTranslationUtils';
import { EventWithParticipants, Event } from '@/types/social';
import { EventFilterOptions } from '@/types/events';
import { useToast } from '@/hooks/use-toast';
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import EventsList from './EventsList';
import EventForm from './EventForm';
import EventsFilter from './EventsFilter';
import { Plus, Calendar } from 'lucide-react';
import { Button } from '@/components/ui/button';

const EventsManagement: React.FC = () => {
  const { t } = useTranslationUtils();
  const { toast } = useToast();
  const [isAddingEvent, setIsAddingEvent] = useState(false);
  const [editingEvent, setEditingEvent] = useState<Event | null>(null);
  const [events, setEvents] = useState<EventWithParticipants[]>([]);
  const [filteredEvents, setFilteredEvents] = useState<EventWithParticipants[]>([]);
  const [loading, setLoading] = useState(true);
  const [filterOptions, setFilterOptions] = useState<EventFilterOptions>({
    showFestiPassOnly: false,
    showUpcomingOnly: true,
    searchQuery: '',
  });

  // Fetch events on component mount
  useEffect(() => {
    fetchEvents();
  }, []);

  // Apply filters whenever filter options or events change
  useEffect(() => {
    applyFilters();
  }, [filterOptions, events]);

  const fetchEvents = async () => {
    try {
      setLoading(true);
      
      const { data, error } = await supabase
        .from('events')
        .select('*')
        .order('start_date', { ascending: false });
        
      if (error) throw error;

      // Format events as EventWithParticipants
      const eventsWithParticipants = await Promise.all(data.map(async (event) => {
        // Get count of participants
        const { count, error: countError } = await supabase
          .from('event_participants')
          .select('id', { count: 'exact', head: true })
          .eq('event_id', event.id);
          
        if (countError) throw countError;
        
        return {
          ...event,
          participants_count: count || 0
        } as EventWithParticipants;
      }));
      
      setEvents(eventsWithParticipants);
    } catch (error) {
      console.error('Error fetching events:', error);
      toast({
        title: t('admin.error'),
        description: t('admin.errorFetchingEvents'),
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...events];
    
    // Filter by FestiPass eligibility
    if (filterOptions.showFestiPassOnly) {
      filtered = filtered.filter(event => event.festi_pass_eligible);
    }
    
    // Filter by upcoming events
    if (filterOptions.showUpcomingOnly) {
      const now = new Date();
      filtered = filtered.filter(event => new Date(event.end_date) > now);
    }
    
    // Filter by search query
    if (filterOptions.searchQuery) {
      const query = filterOptions.searchQuery.toLowerCase();
      filtered = filtered.filter(
        event => 
          event.name.toLowerCase().includes(query) ||
          event.location.toLowerCase().includes(query) ||
          (event.description && event.description.toLowerCase().includes(query))
      );
    }
    
    setFilteredEvents(filtered);
  };

  const handleFilterChange = (newFilters: Partial<EventFilterOptions>) => {
    setFilterOptions(prev => ({ ...prev, ...newFilters }));
  };

  const handleAddEvent = () => {
    setEditingEvent(null);
    setIsAddingEvent(true);
  };

  const handleEditEvent = (event: Event) => {
    setEditingEvent(event);
    setIsAddingEvent(true);
  };

  const handleCancelForm = () => {
    setIsAddingEvent(false);
    setEditingEvent(null);
  };

  const handleEventSaved = () => {
    setIsAddingEvent(false);
    setEditingEvent(null);
    fetchEvents();
    toast({
      title: t('admin.success'),
      description: editingEvent 
        ? t('admin.eventUpdated') 
        : t('admin.eventCreated'),
    });
  };

  const handleDeleteEvent = async (eventId: string) => {
    if (!confirm(t('admin.confirmEventDelete'))) return;
    
    try {
      // First delete any FestiPass details if they exist
      const { error: festiPassError } = await supabase
        .from('festi_pass')
        .delete()
        .eq('event_id', eventId);
        
      if (festiPassError) throw festiPassError;
      
      // Then delete any event participants
      const { error: participantsError } = await supabase
        .from('event_participants')
        .delete()
        .eq('event_id', eventId);
        
      if (participantsError) throw participantsError;
      
      // Finally delete the event
      const { error } = await supabase
        .from('events')
        .delete()
        .eq('id', eventId);
        
      if (error) throw error;
      
      fetchEvents();
      toast({
        title: t('admin.success'),
        description: t('admin.eventDeleted'),
      });
    } catch (error) {
      console.error('Error deleting event:', error);
      toast({
        title: t('admin.error'),
        description: t('admin.errorDeletingEvent'),
        variant: 'destructive',
      });
    }
  };

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="flex items-center gap-2">
          <Calendar className="h-5 w-5" />
          <span>{t('admin.events')}</span>
        </CardTitle>
        
        {!isAddingEvent && (
          <Button 
            onClick={handleAddEvent} 
            className="flex items-center gap-1"
          >
            <Plus size={16} />
            <span>{t('events.createEvent')}</span>
          </Button>
        )}
      </CardHeader>
      
      <CardContent>
        {isAddingEvent ? (
          <EventForm 
            editingEvent={editingEvent}
            onCancel={handleCancelForm}
            onSave={handleEventSaved}
          />
        ) : (
          <>
            <EventsFilter 
              filterOptions={filterOptions}
              onFilterChange={handleFilterChange}
            />
            
            <div className="mt-4">
              <EventsList 
                events={filteredEvents}
                loading={loading}
                onEdit={handleEditEvent}
                onDelete={handleDeleteEvent}
              />
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default EventsManagement;
